import { useState, type ReactNode } from 'react';
import { Etiquette, Montant } from '../../composants/communs.tsx';
import { formaterFcfa } from '../../composants/ui/index.ts';
import { libelleMode } from './modes.ts';
import { MODES, type Reglement } from './vente-types.ts';

/** Montants fréquents en espèces (CFA) — repères rapides, pas une liste exhaustive. */
const COUPURES_COURANTES = [1000, 2000, 5000, 10000, 20000];

/** Règlements et calcul de l'encaissé / du reste — RG-078, RG-081. */
export function Encaissement({
  reglements,
  onAjouter,
  onRetirer,
  manque,
  rendu,
  encaisse,
  total,
  aCredit,
  onACredit,
  panierVide,
  enregistrement,
  onEncaisser,
  photoFichier,
  onPhotoFichier,
}: {
  reglements: Reglement[];
  onAjouter: (reglement: Reglement) => void;
  onRetirer: (index: number) => void;
  manque: number;
  rendu: number;
  encaisse: number;
  total: number;
  aCredit: boolean;
  onACredit: (valeur: boolean) => void;
  panierVide: boolean;
  enregistrement: boolean;
  onEncaisser: () => void;
  /** Photo du panier — facultative, sauf boutique l'exigeant (RG-168). */
  photoFichier: File | null;
  onPhotoFichier: (fichier: File | null) => void;
}): ReactNode {
  const [mode, setMode] = useState<string>('ESPECES');
  const [montant, setMontant] = useState(0);

  const montantsRapides = [
    { libelle: `Montant exact · ${formaterFcfa(Math.max(0, manque))}`, valeur: Math.max(0, manque) },
    ...COUPURES_COURANTES.map((valeur) => ({ libelle: formaterFcfa(valeur), valeur })),
  ];

  return (
    <div className="vente-section">
      <div className="vente-section__tete">
        <h2>Récapitulatif</h2>
      </div>

      <div className="pile" style={{ gap: 4 }}>
        <div className="ligne" style={{ justifyContent: 'space-between' }}>
          <span>Sous-total</span>
          <Montant valeur={total} />
        </div>
      </div>

      <div className="total-a-payer">
        <span>Total à payer</span>
        <span className="total-a-payer__valeur">
          <Montant valeur={total} />
        </span>
      </div>

      <h2 style={{ marginTop: 'var(--espace-4)' }}>Mode de règlement</h2>
      <div className="modes-paiement">
        {MODES.map((valeur) => (
          <button
            key={valeur}
            type="button"
            className={`mode-paiement${mode === valeur ? ' mode-paiement--actif' : ''}`}
            onClick={() => {
              setMode(valeur);
            }}
          >
            {libelleMode(valeur)}
          </button>
        ))}
      </div>

      <label htmlFor="vente-montant-recu">Montant reçu</label>
      <div className="ligne">
        <input
          id="vente-montant-recu"
          type="number"
          min={1}
          step={1}
          value={montant === 0 ? '' : montant}
          onChange={(evenement) => {
            setMontant(Math.max(0, Number(evenement.target.value) || 0));
          }}
        />
        <button
          type="button"
          onClick={() => {
            if (montant <= 0) return;
            onAjouter({ mode, montant });
            setMontant(0);
          }}
        >
          Ajouter
        </button>
      </div>

      <div className="montants-rapides">
        {montantsRapides
          .filter((rapide) => rapide.valeur > 0)
          .map((rapide) => (
            <button
              key={rapide.libelle}
              type="button"
              className="montant-rapide"
              onClick={() => {
                setMontant(rapide.valeur);
              }}
            >
              {rapide.libelle}
            </button>
          ))}
      </div>

      {reglements.length > 0 && (
        <div className="pile" style={{ gap: 4, marginBottom: 12 }}>
          {reglements.map((reglement, index) => (
            <div key={`${reglement.mode}-${String(index)}`} className="ligne" style={{ justifyContent: 'space-between' }}>
              <span>{libelleMode(reglement.mode)}</span>
              <span className="ligne">
                <Montant valeur={reglement.montant} />
                <button
                  type="button"
                  className="bouton--discret"
                  onClick={() => {
                    onRetirer(index);
                  }}
                >
                  Retirer
                </button>
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="pile" style={{ gap: 4 }}>
        <div className="ligne" style={{ justifyContent: 'space-between' }}>
          <span>Encaissé</span>
          <Montant valeur={encaisse} />
        </div>
        {manque > 0 && (
          <div className="ligne" style={{ justifyContent: 'space-between' }}>
            <span>Reste à payer</span>
            <Etiquette ton="ambre" texte={formaterFcfa(manque)} />
          </div>
        )}
        {manque < 0 && (
          <div className="ligne" style={{ justifyContent: 'space-between' }}>
            <span>Monnaie à rendre</span>
            <Montant valeur={rendu} />
          </div>
        )}
      </div>

      {manque > 0 && (
        <label style={{ marginTop: 8, display: 'block' }}>
          <input
            type="checkbox"
            style={{ marginRight: 8 }}
            checked={aCredit}
            onChange={(evenement) => {
              onACredit(evenement.target.checked);
            }}
          />
          Vente à crédit — le reste devient une créance sur le client sélectionné
        </label>
      )}

      <div className="champ" style={{ marginTop: 'var(--espace-4)' }}>
        <label htmlFor="vente-photo-panier">
          Photo du panier {photoFichier === null ? '(facultatif, sauf boutique l’exigeant)' : ''}
        </label>
        {photoFichier === null ? (
          <input
            id="vente-photo-panier"
            type="file"
            accept="image/*"
            onChange={(evenement) => {
              onPhotoFichier(evenement.target.files?.[0] ?? null);
            }}
          />
        ) : (
          <div className="ligne" style={{ justifyContent: 'space-between' }}>
            <span className="entete__sous">{photoFichier.name}</span>
            <button
              type="button"
              className="bouton--discret"
              onClick={() => {
                onPhotoFichier(null);
              }}
            >
              Retirer
            </button>
          </div>
        )}
      </div>

      <button
        type="button"
        style={{ width: '100%', justifyContent: 'center', marginTop: 'var(--espace-3)' }}
        disabled={enregistrement || panierVide}
        onClick={onEncaisser}
      >
        {enregistrement
          ? 'Enregistrement…'
          : manque > 0
            ? `Encaisser encore ${formaterFcfa(manque)}`
            : 'Enregistrer la vente'}
      </button>
    </div>
  );
}
